import { Images } from '@/assets/images';
import { DocTypeEnum, REGEX } from '@/constant';
import { convertVisaCodeToName, formatDate } from '@/utils';

import dayjs from 'dayjs';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import { FormConfig } from '../define-document-write.type';

const initializeValue = {
	applicantName: '',
	nationalCode: '',
	applicantGender: '',
	foreignerNo: '',
	residenceCode: '',
	passportNo: '',
	applicantPhone: '',
	applicationKoreaAddress: '',
	applicationDesiredWorkplace: '',
	applicationDesiredPeriod: '',
	applicationRegisterYear: '',
	applicationRegisterYearEn: '',
	applicationRegisterYearCn: '',
	applicationDateApplication: '',
	applicationRegisterNameStay: '',
	applicationSignatureStay: '',
};

type IValues = typeof initializeValue;

export const DOMESTIC_STAY_CONFIG: FormConfig<IValues, DocTypeEnum.PM1_19> = {
	initializeValue,
	title: '계절근로 참여 신청서 (국내체류 외국인)',
	mapPropToValues({
		applicantData,
		lodgingData,
		applicantLodgingData,
		detailData,
	}) {
		const unitInfo = detailData?.unitInfo;

		const currentYear = dayjs().year().toString();
		return {
			...initializeValue,
			applicantName: applicantData?.fullName || '',
			nationalCode: applicantData?.nationalName || '',
			applicantGender: isNotNilOrEmpty(applicantData?.genderName)
				? applicantData?.genderName === '남'
					? '남자'
					: '여자'
				: '',
			foreignerNo: isNotNilOrEmpty(applicantData?.birthDate)
				? `${formatDate(applicantData?.birthDate, {
						outputFormat: 'YYMMDD',
					})}-`
				: '',
			residenceCode: applicantData?.visaCode
				? convertVisaCodeToName(applicantData?.visaCode)
				: '',
			passportNo: applicantData?.passportNo || '',
			applicantPhone: applicantData?.phone || '',
			applicationKoreaAddress: lodgingData?.address
				? lodgingData?.address
				: applicantLodgingData?.lodgingAddress || '',
			applicationRegisterNameStay: applicantData?.fullName || '',
			applicationDesiredPeriod: unitInfo?.countyDistrict || '',
			applicationRegisterYear: currentYear,

			applicationDateApplication: formatDate(dayjs()),
		};
	},
	pages: [
		{
			background: {
				src: Images.formDomesticStay,
				className: 'template',
				dataSource: Images.formDomesticStay,
			},

			textAreas: [
				{
					name: 'applicantName',
					maxHeight: 42,
					maxLength: 128,
					containerClassName: 'absolute top-[283px] left-[168px] w-[156px]',
				},
				{
					name: 'nationalCode',
					maxHeight: 42,
					maxLength: 30,
					containerClassName: 'absolute top-[282px] left-[422px] w-[99px]',
				},
				{
					name: 'foreignerNo',
					maxHeight: 42,
					maxLength: 20,
					containerClassName: 'absolute top-[341px] left-[325px] w-[196px]',
				},
				{
					name: 'residenceCode',
					maxHeight: 42,
					maxLength: 20,
					containerClassName: 'absolute top-[341px] right-[78px] w-[99px]',
				},
				{
					name: 'passportNo',
					maxHeight: 42,
					maxLength: 40,
					containerClassName: 'absolute top-[401px] left-[325px] w-[196px]',
				},
				{
					name: 'applicantPhone',
					maxHeight: 42,
					maxLength: 40,
					containerClassName: 'absolute top-[401px] right-[78px] w-[100px]',
				},
				{
					name: 'applicationKoreaAddress',
					maxHeight: 42,
					maxLength: 40,
					containerClassName: 'absolute top-[460px] left-[330px] w-[385px]',
				},
				{
					name: 'applicationDesiredWorkplace',
					maxHeight: 56,
					maxLength: 100,
					containerClassName: 'absolute top-[610px] left-[304px] w-[410px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'applicationDesiredPeriod',
					maxHeight: 56,
					maxLength: 100,
					containerClassName: 'absolute top-[668px] left-[304px] w-[410px]',
				},
				{
					name: 'applicationRegisterYear',
					maxHeight: 22,
					maxLength: 4,
					containerClassName: 'absolute bottom-[317px] left-[293px] w-[60px]',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'applicationRegisterYear',
					maxHeight: 22,
					maxLength: 4,
					containerClassName: 'absolute bottom-[282px] right-[107px] w-[60px]',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'applicationRegisterYear',
					maxHeight: 22,
					maxLength: 4,
					containerClassName: 'absolute bottom-[249px] left-[384px] w-[60px]',
					regex: REGEX.ALLOW_NUMBER,
				},
				{
					name: 'applicationRegisterNameStay',
					maxHeight: 53,
					maxLength: 63,
					containerClassName: 'absolute w-[235px] top-[983px] left-[265px]',
				},
			],
			checkboxes: [
				{
					name: 'applicantGender',
					classNames: [
						'absolute top-[286px] right-[149px]',
						'absolute top-[304px] right-[149px]',
					],
					iconType: 'circle',
					options: ['남자', '여자'],
				},
			],
			datePickers: [
				{
					name: 'applicationDateApplication',
					className: '[&>.ant-picker-input>input]:!text-[14px]',
					containerClassName: 'absolute bottom-[102px] left-[132px] w-[103px]',
				},
			],
			signatures: [
				{
					name: 'applicationSignatureStay',
					className: 'absolute bottom-[86px] right-[138px] w-[85px] h-[50px]',
				},
			],
			highlightBoxes: [
				{
					name: 'applicantName',
					className: 'top-[276px] left-[94px] w-[58px] h-[26px]',
				},
				{
					name: 'nationalCode',
					className: 'top-[276px] left-[349px] w-[49px] h-[26px]',
				},
				{
					name: 'applicantGender',
					className: 'top-[276px] left-[546px] w-[49px] h-[26px]',
				},
				(values, index) => ({
					name: 'foreignerNo',
					className: 'top-[336px] left-[99px] w-[208px] h-[26px]',
					hasError: values?.foreignerNo?.length < 13,
				}),
				{
					name: 'residenceCode',
					className: 'top-[336px] left-[535px] w-[67px] h-[25px]',
				},
				{
					name: 'passportNo',
					className: 'top-[395px] left-[138px] w-[130px] h-[25px]',
				},
				{
					name: 'applicantPhone',
					className: 'top-[395px] left-[535px] w-[68px] h-[25px]',
				},
				{
					name: 'applicationKoreaAddress',
					className: 'top-[455px] left-[128px] w-[148px] h-[25px]',
				},
				{
					name: 'applicationDesiredWorkplace',
					className: 'top-[611px] left-[95px] w-[114px] h-[25px]',
				},
				{
					name: 'applicationDesiredPeriod',
					className: 'top-[671px] left-[95px] w-[117px] h-[25px]',
				},
				{
					name: 'applicationRegisterYear',
					className: 'top-[779px] left-[274px] w-[62px] h-[22px]',
				},
				{
					name: 'applicationRegisterYear',
					className: 'top-[813px] left-[612px] w-[62px] h-[22px]',
				},
				{
					name: 'applicationRegisterYear',
					className: 'top-[846px] left-[367px] w-[60px] h-[22px]',
				},
				{
					name: 'applicationDateApplication',
					className: 'top-[920px] left-[128px] w-[81px] h-[27px]',
				},
				{
					name: 'applicationRegisterNameStay',
					className: 'top-[920px] left-[350px] w-[65px] h-[27px]',
				},
				{
					name: 'applicationSignatureStay',
					className: 'top-[920px] right-[122px] w-[118px] h-[27px]',
				},
			],
		},
	],
};
